import {
  EHeaderTap,
  MatchHeaderTabBarComponent,
} from '@/app/components/shared/match-header-tab-bar/match-header-tab-bar.component';
import { NoResultComponent } from '@/app/components/shared/no-result/no-result.component';
import { SkeletonPreviewDetailsComponent } from '@/app/components/shared/skeleton';
import { useWsMatchMatches } from '@/app/core/websocket/hooks';
import {
  EGamePeriod,
  EGoogleTagAnalyticsKeys,
  EStatusMatches,
} from '@/app/pages/sports/football/enums';
import { PerformanceService } from '@/app/services/performance.service';
import { RouteValidatorService } from '@/app/services/route-validator.service';
import { TranslationService } from '@/app/services/translation.service';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { FootballBreadcrumbsComponent } from '@/app/components/shared/football-breadcrumbs/football-breadcrumbs.component';
import { SeoService } from '@/app/services/seo';
import { GoogleTagAnalyticsService } from '../../../../services/google-tag/google-tag-analytics.service';
import { MatchDetailsComponent } from './match-details/match-details.component';
import { MatchSummary } from './models';
import { MatchSummaryService } from './services';
import { H2hComponent, MatchComponent, StandingComponent } from './tabs';

const headerTap = {
  0: 'MATCH',
  1: 'H2H',
  2: 'CLASIFICACION',
};

@Component({
  selector: 'app-football-match-details',
  standalone: true,
  imports: [
    MatchHeaderTabBarComponent,
    MatchComponent,
    H2hComponent,
    StandingComponent,
    MatchDetailsComponent,
    SkeletonPreviewDetailsComponent,
    NoResultComponent,
    FootballBreadcrumbsComponent,
  ],
  templateUrl: './match-details.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FootballMatchDetailsComponent {
  private googleTagAnalyticsService = inject(GoogleTagAnalyticsService);
  private performanceService = inject(PerformanceService);
  private activeRoute = inject(ActivatedRoute);
  private router = inject(Router);
  private translationService = inject(TranslationService);
  private seoService = inject(SeoService);
  private refreshInterval!: ReturnType<typeof setInterval>;

  wsMatchMatches = useWsMatchMatches();
  selectedHeaderTab = signal<EHeaderTap>(EHeaderTap.MATCH);

  // paramsID
  matchId = signal<string | null>(null);
  slug = signal<string | null>(null);

  routerValidatorService = inject(RouteValidatorService);
  matchSummaryService = inject(MatchSummaryService);

  baseMatchSummary = computed(() => this.matchSummaryService.matchSummary());
  wsMatchData = effect(
    () => {
      if (!this.matchId() || !this.wsMatchMatches.data()[this.matchId()!])
        return;

      this.matchSummaryService.setWsMatch(
        this.wsMatchMatches.data()[this.matchId()!]
      );
    },
    { allowSignalWrites: true }
  );

  readonly liveMatchSummary = computed(() => {
    const wsMatch = this.matchSummaryService.wsMatch();

    const newMatchSummary: MatchSummary | null =
      wsMatch && this.baseMatchSummary()
        ? {
            ...this.baseMatchSummary()!,
            MatchGamePeriod:
              wsMatch.gamePeriod ?? this.baseMatchSummary()!.MatchGamePeriod,
            State: wsMatch.state,
            PeriodStartTime:
              wsMatch.periodStartTime !== null
                ? wsMatch.periodStartTime.toISOString()
                : this.baseMatchSummary()!.PeriodStartTime,
            GameTimeAdd: wsMatch.gameTimeAdd,
            HomeTeam: {
              ...this.baseMatchSummary()!.HomeTeam,
              Scores: {
                ...this.baseMatchSummary()!.HomeTeam.Scores,
                FinalScore: wsMatch.homeScore,
                HalfScore: wsMatch.halfHomeScore,
              },
            },
            AwayTeam: {
              ...this.baseMatchSummary()!.AwayTeam,
              Scores: {
                ...this.baseMatchSummary()!.AwayTeam.Scores,
                FinalScore: wsMatch.awayScore,
                HalfScore: wsMatch.halfAwayScore,
              },
            },
          }
        : this.baseMatchSummary();

    // this.baseMatchSummary = signal(newMatchSummary);

    return newMatchSummary;
  });

  constructor() {
    window.scrollTo(0, 0);

    this.activeRoute.paramMap.subscribe((params) => {
      const id = params.get('matchId');
      const slug = params.get('slug');

      if (id) {
        this.matchId.set(id);
        this.slug.set(slug);
        this.matchSummaryService.getMatchSummary(id, () => {
          if (slug) {
            this.validateSlug();
          }

          // insert SEO
          const homeTeam = this.translationService.getTraduccionName(
            this.baseMatchSummary()?.HomeTeam?.NameTranslation!,
            this.baseMatchSummary()?.HomeTeam.Name || 'NA'
          );
          const awayTeam = this.translationService.getTraduccionName(
            this.baseMatchSummary()?.AwayTeam?.NameTranslation!,
            this.baseMatchSummary()?.AwayTeam.Name || 'NA'
          );
          const url = window.location.href;

          this.seoService.setMatchSeo({
            homeTeam,
            awayTeam,
            date: this.baseMatchSummary()?.MatchStart || '',
            location: '',
            url,
            // image: '/assets/match.jpg',
          });
        });
        this.selectedHeaderTab.set(EHeaderTap.MATCH);
        // google tag
        this.googleTagAnalyticsService.event(
          EGoogleTagAnalyticsKeys.MATCH_DETAILS_CURRENT_TAB,
          {
            tab: headerTap[0],
          }
        );

        if (
          this.matchSummaryService.matchSummary()?.State ===
          EStatusMatches.NORMAL
        ) {
          // if (this.baseMatchSummary()?.State === EStatusMatches.NORMAL) {
          this.refreshInterval = setInterval(() => {
            this.matchSummaryService.getMatchSummary(
              this.matchId()!,
              () => {},
              true
            );
          }, 1000 * 60 * 5); // cada 5 minutos
        }
      }
    });

    // if (this.matchId())
    //   this.matchSummaryService.getMatchSummary(this.matchId()!);
  }

  ngAfterViewInit() {
    this.performanceService.activate();
  }

  handleHeaderTap(tabValue: EHeaderTap) {
    // google tag
    this.googleTagAnalyticsService.event(
      EGoogleTagAnalyticsKeys.MATCH_DETAILS_CURRENT_TAB,
      {
        tab: headerTap[tabValue] || tabValue,
      }
    );

    this.selectedHeaderTab.set(tabValue);
  }

  getGamePeriod(): EGamePeriod | undefined {
    if (this.liveMatchSummary()?.State === EStatusMatches.NORMAL) {
      return this.liveMatchSummary()?.MatchGamePeriod;
    }

    return undefined;
  }

  validateSlug() {
    const { AwayTeam, HomeTeam } = this.baseMatchSummary()!;

    const correctSlug = this.translationService.slugifyNameRoute(
      {
        NameTranslation: HomeTeam.NameTranslation,
        defaultName: HomeTeam.Name,
      },
      {
        NameTranslation: AwayTeam.NameTranslation,
        defaultName: AwayTeam.Name,
      }
    );
    // if (correctSlug !== this.slug()) {
    // const lang = this.translationService.getLangLocalStorage();
    // const pathInit = lang === 'es' ? 'futbol/partido' : 'football/match';

    // Redirigir a la URL correcta
    this.router.navigate(
      [
        this.translationService.getInitPathFootballMath(),
        correctSlug,
        this.matchId(),
      ],
      {
        replaceUrl: true,
        queryParamsHandling: 'preserve',
      }
    );
    // }
  }

  ngOnDestroy() {
    this.performanceService.deactivate();

    clearInterval(this.refreshInterval);
    this.wsMatchMatches.disconnect();
    this.matchSummaryService.wsMatch.set(null);
  }
}
